import React, { useEffect, useState } from 'react';
import {
  AccordionContent,
  AccordionContentWrapper,
  AccordionHeader,
  AccordionStyled,
  AccordionSubtitle,
  AccordionSubtitleWrapper,
  AccordionTitle,
} from './Accordion.styles';
import { CollapseIcon } from './CollapseIcon';

interface Props {
  children: React.ReactNode;
  defaultOpen?: boolean;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  last?: boolean; // set to true if it's a single accordion or the last of multiple
  topBorder?: boolean;
  bottomBorder?: boolean;
  shadow?: boolean;
  topBorderRadius?: string;
  bottomBorderRadius?: string;
  'data-testid'?: string;
  className?: string;
  showError?: boolean;
  errorText?: string;
  onClick?: () => void;
  openCallback?: () => void;
  accordionRef?: React.Ref<HTMLDivElement>;
  preventChange?: boolean;
  customCollapseIconOpen?: React.ReactNode;
  customCollapseIconClosed?: React.ReactNode;
  backgroundColor?: string;
  bodyBackgroundColor?: string;
  borderColor?: string;
  hoverColor?: string;
  headerProps?: React.HTMLAttributes<HTMLDivElement> | null;
  isDragging?: boolean;
  containerBorderColor?: string;
  hideContentBorder?: boolean;
}

export const Accordion = ({
  children,
  defaultOpen = false,
  title,
  subtitle,
  last,
  topBorderRadius = '6px',
  bottomBorderRadius = '6px',
  className,
  showError,
  onClick,
  openCallback,
  accordionRef,
  preventChange,
  customCollapseIconOpen,
  customCollapseIconClosed,
  bottomBorder = true,
  backgroundColor,
  bodyBackgroundColor,
  borderColor,
  hoverColor,
  headerProps,
  isDragging,
  hideContentBorder,
  ...props
}: Props) => {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    openCallback?.();
  }, [open, openCallback]);

  useEffect(() => {
    if (!preventChange) {
      setOpen(defaultOpen);
    }
  }, [defaultOpen, preventChange]);

  return (
    <AccordionStyled
      className={className}
      open={open}
      bottomBorderRadius={bottomBorderRadius}
      topBorderRadius={topBorderRadius}
      bodyBackgroundColor={bodyBackgroundColor}
      showError={showError}
      ref={accordionRef}
      {...props}
    >
      <AccordionHeader
        open={open}
        onClick={() => {
          if (!preventChange && !isDragging) {
            setOpen(!open);
          }
          onClick?.();
        }}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        hoverColor={hoverColor}
        data-testid={`${props['data-testid']}-header`}
        bottomBorderRadius={bottomBorderRadius}
        topBorderRadius={topBorderRadius}
        showError={showError}
        bottomBorder={bottomBorder}
        {...headerProps}
      >
        {typeof title === 'string' ? (
          <AccordionTitle>{title}</AccordionTitle>
        ) : (
          title
        )}
        <AccordionSubtitleWrapper>
          {subtitle &&
            (typeof subtitle === 'string' ? (
              <AccordionSubtitle>{subtitle}</AccordionSubtitle>
            ) : (
              subtitle
            ))}
          <CollapseIcon
            open={open}
            customCollapseIconOpen={customCollapseIconOpen}
            customCollapseIconClosed={customCollapseIconClosed}
          />
        </AccordionSubtitleWrapper>
      </AccordionHeader>
      <AccordionContentWrapper
        open={open}
        last={last}
        bottomBorderRadius={bottomBorderRadius}
        hideContentBorder={hideContentBorder}
      >
        <AccordionContent
          open={open}
          last={last}
          bottomBorderRadius={bottomBorderRadius}
          data-testid={`${props['data-testid']}-body`}
          backgroundColor={bodyBackgroundColor}
        >
          <div id={`${props['data-testid']}-children-container`}>
            {React.Children.map(children, (child) => {
              if (React.isValidElement(child) && !open) {
                return React.cloneElement(
                  child as React.ReactElement<{ tabIndex?: number }>,
                  {
                    tabIndex: -1,
                  },
                );
              }

              return child;
            })}
          </div>
        </AccordionContent>
      </AccordionContentWrapper>
    </AccordionStyled>
  );
};
